import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { ATTACKERS, DEFENDERS } from "@/data/operators";
import { useViewerHeartbeat } from "@/hooks/useViewerHeartbeat";
import LiveViewerPill from "@/components/LiveViewerPill";

/**
 * Shared page shell: watermark + overlays + header + footer.
 * Children render inside the main content area.
 */
export const PageShell = ({ children, backTo = "/", hideBack = false, title = "Random Six Siege", subtitle = "Tactical Dispatch v2.6", rightSlot = null }) => {
  useViewerHeartbeat();
  const location = useLocation();
  const isHome = location.pathname === "/";
  return (
    <div className="min-h-screen bg-[#080808] text-white relative overflow-x-hidden">
      {/* Subtle gold glow in top-right — echoes the logo aesthetic without overwhelming */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 100% 0%, rgba(255,200,0,0.07) 0%, rgba(255,200,0,0) 60%)",
        }}
      />
      {/* Faint R6 logo watermark */}
      <div
        className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center overflow-hidden"
        aria-hidden="true"
        data-testid="r6-watermark"
      >
        <img
          src="/r6-watermark.png"
          alt=""
          draggable={false}
          className="select-none"
          style={{
            width: "min(92vmin, 780px)",
            height: "min(92vmin, 780px)",
            objectFit: "contain",
            opacity: 0.055,
            imageRendering: "auto",
          }}
        />
      </div>
      {/* Grain overlay */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.04] mix-blend-overlay z-0"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")',
        }}
      />
      {/* Scanlines */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.05] z-0"
        style={{
          background:
            "repeating-linear-gradient(0deg, transparent 0 2px, rgba(255,255,255,0.4) 2px 3px)",
        }}
      />

      {/* Header */}
      <header className="relative z-10 border-b border-neutral-900/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {!isHome && !hideBack && (
              <Link
                to={backTo}
                data-testid="back-to-home-btn"
                className="flex items-center gap-1 text-neutral-400 hover:text-white transition-colors px-2 py-1 -ml-2"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                <ChevronLeft size={16} />
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold hidden sm:inline">
                  Back
                </span>
              </Link>
            )}
            <Link to="/" className="flex items-center gap-3 min-w-0">
              <img
                src="https://customer-assets.emergentagent.com/job_r6-random-pick/artifacts/4wzi2ph5_294a61ed-36f8-4713-b039-c1f1fde0f2d4_removalai_preview.png"
                alt="Random Six Siege"
                draggable={false}
                className="h-9 w-auto shrink-0 select-none"
                style={{ objectFit: "contain" }}
              />
              <div className="min-w-0">
                <div
                  className="text-white text-lg font-black tracking-tight uppercase leading-none truncate"
                  style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
                >
                  {title}
                </div>
                <div
                  className="text-[10px] text-neutral-500 tracking-[0.3em] uppercase truncate"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  {subtitle}
                </div>
              </div>
            </Link>
          </div>
          {rightSlot}
        </div>
      </header>

      <main className="relative z-10">{children}</main>

      <footer className="relative z-10 border-t border-neutral-900/80 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div
            className="text-[10px] tracking-[0.3em] uppercase text-neutral-600"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            Unofficial fan tool · not affiliated with Ubisoft
          </div>
          <div
            className="flex items-center gap-4 text-[10px] tracking-[0.3em] uppercase"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="text-neutral-500 hover:text-white transition-colors"
            >
              Privacy
            </Link>
            <span className="text-neutral-800">·</span>
            <Link
              to="/terms"
              data-testid="footer-terms-link"
              className="text-neutral-500 hover:text-white transition-colors"
            >
              Terms
            </Link>
            <span className="text-neutral-800">·</span>
            <span className="text-neutral-600">{ATTACKERS.length + DEFENDERS.length} OPS LOADED</span>
          </div>
        </div>
      </footer>
      <LiveViewerPill />
    </div>
  );
};

export default PageShell;
